import { Content } from "antd/es/layout/layout";
import React, { useEffect, useMemo, useState } from "react";
import "./style.css";
import { Avatar, Button, Form, Input, Modal, Skeleton, message } from "antd";
import {
  SafetyCertificateOutlined,
  CheckCircleFilled,
  ClockCircleOutlined,
  LockOutlined,
  UserOutlined,
  BankFilled,
  LeftOutlined,
} from "@ant-design/icons";
import { generateClient } from "aws-amplify/api";
import { useNavigate } from "react-router-dom";
import { fetchUserAttributes, updatePassword } from "@aws-amplify/auth";
import canadaFlag from "../Image/ca-circle.png";
import barbadosFlag from "../Image/flag/barbados.png";
import { getUser } from "../graphql/queries";
import { getTableID } from "../Services/authServices";
import { getVerificationUiState } from "../Utils/verificationStatus";

const client = generateClient();

const MyAccount = () => {
  const navigate = useNavigate();
  const [passwordForm] = Form.useForm();
  const [loading, setLoading] = useState(true);
  const [passwordModalOpen, setPasswordModalOpen] = useState(false);
  const [passwordSubmitting, setPasswordSubmitting] = useState(false);
  const [userData, setUserData] = useState({
    name: "",
    phoneNumber: "",
    email: "",
    accountType: "",
    businessName: "",
    country: "",
  });
  const [verification, setVerification] = useState({
    identificationVerified: false,
    verificationStatus: "",
    isOrganization: false,
  });

  useEffect(() => {
    const fetchUserInformation = async () => {
      try {
        setLoading(true);
        const tableID = await getTableID();
        if (!tableID) return;
        const [{ data }, userAttributes] = await Promise.all([
          client.graphql({
            query: getUser,
            variables: { id: tableID },
          }),
          fetchUserAttributes(),
        ]);
        const status = data?.getUser || {};
        const customAccountType = String(
          userAttributes?.["custom:accountType"] || "",
        )
          .trim()
          .toLowerCase();
        const accountType = customAccountType
          ? customAccountType.charAt(0).toUpperCase() +
            customAccountType.slice(1)
          : status?.isOrganization
            ? "Business"
            : "Personal";
        const nextData = {
          name: status?.name || "",
          phoneNumber: status?.phoneNumber || "",
          email: status?.email || "",
          accountType,
          businessName:
            status?.businessName ||
            userAttributes?.["custom:businessName"] ||
            "",
          country: userAttributes?.["custom:country"] || "N/A",
        };

        setUserData(nextData);
        setVerification({
          identificationVerified: !!status?.identificationVerified,
          verificationStatus: status?.verificationStatus || "",
          isOrganization: !!status?.isOrganization,
        });
      } catch (error) {
        console.error("Error loading account details:", error);
      } finally {
        setLoading(false);
      }
    };
    fetchUserInformation();
  }, []);

  const initials = useMemo(() => {
    const isBusiness =
      String(userData.accountType || "").toLowerCase() === "business";
    const displayName = String(
      isBusiness && userData.businessName
        ? userData.businessName
        : userData.name || "",
    ).trim();
    if (!displayName) return "U";
    return displayName
      .split(" ")
      .filter(Boolean)
      .slice(0, 2)
      .map((part) => part[0].toUpperCase())
      .join("");
  }, [userData.accountType, userData.businessName, userData.name]);

  const primaryDisplayName =
    String(userData.accountType || "").toLowerCase() === "business" &&
    userData.businessName
      ? userData.businessName
      : userData.name || "Account Holder";

  const verificationUi = useMemo(
    () =>
      getVerificationUiState({
        verificationStatus: verification.verificationStatus,
        identificationVerified: verification.identificationVerified,
        isOrganization: verification.isOrganization,
      }),
    [verification],
  );

  const handleStartVerification = () => {
    navigate("/kycuser");
  };

  const handleChangePassword = async () => {
    try {
      const values = await passwordForm.validateFields();
      setPasswordSubmitting(true);
      await updatePassword({
        oldPassword: values.currentPassword,
        newPassword: values.newPassword,
      });
      message.success("Password updated successfully.");
      setPasswordModalOpen(false);
      passwordForm.resetFields();
    } catch (error) {
      if (error?.errorFields) return;
      const fallbackMessage = "Unable to update password.";
      const errorMessage =
        error?.message ||
        String(error || "")
          .split(":")
          .pop()
          .trim() ||
        fallbackMessage;
      message.error(errorMessage || fallbackMessage);
    } finally {
      setPasswordSubmitting(false);
    }
  };

  const formatPhoneNumber = (value) => {
    const digits = String(value || "").replace(/\D/g, "");
    if (!digits) return "N/A";
    if (digits.startsWith("1246") && digits.length === 11) {
      const local = digits.slice(4);
      return `+1 246 ${local.slice(0, 3)}-${local.slice(3)}`;
    }
    if (digits.startsWith("1") && digits.length === 11) {
      const local = digits.slice(1);
      return `+1 (${local.slice(0, 3)}) ${local.slice(3, 6)}-${local.slice(6)}`;
    }
    if (digits.length === 7) {
      return `${digits.slice(0, 3)}-${digits.slice(3)}`;
    }
    return value || "N/A";
  };

  const getCountryFlag = (country) => {
    const normalized = String(country || "")
      .trim()
      .toLowerCase();
    if (normalized === "canada") return canadaFlag;
    if (normalized === "barbados") return barbadosFlag;
    return null;
  };

  return (
    <Content className="appPageContent">
      <div className="appPageShell appPageShellNarrow">
        <div className="appSectionHeader appPageHeader">
          <Button
            type="text"
            icon={<LeftOutlined />}
            className="backButton"
            onClick={() => navigate(-1)}
          />
          <h2 className="appSectionTitle">My Account</h2>
        </div>
        <div className="appTableCard myAccountCard">
          {loading ? (
            <Skeleton active paragraph={{ rows: 4 }} />
          ) : (
            <>
              {!verificationUi.isVerified ? (
                <div
                  className={`myAccountVerificationCard myAccountVerificationCard${verificationUi.cardTone.charAt(0).toUpperCase()}${verificationUi.cardTone.slice(1)}`}
                >
                  <div className="myAccountVerificationIntro">
                    <span className="myAccountVerificationIntroIcon">
                      {verificationUi.pillTone === "rejected" ? (
                        <ClockCircleOutlined />
                      ) : (
                        <SafetyCertificateOutlined />
                      )}
                    </span>
                    <div className="myAccountVerificationIntroText">
                      <h4>{verificationUi.cardTitle}</h4>
                      <p>{verificationUi.cardText}</p>
                    </div>
                  </div>
                </div>
              ) : null}

              <div className="myAccountProfileRow">
                <Avatar
                  size={58}
                  className="myAccountAvatar"
                  icon={!initials ? <UserOutlined /> : null}
                >
                  {initials || null}
                </Avatar>
                <div className="myAccountProfileText">
                  <div className="myAccountProfileTitleRow">
                    <h3>{primaryDisplayName}</h3>
                    <span
                      className={`myAccountVerificationPill ${
                        verificationUi.pillTone === "verified"
                          ? "myAccountVerificationPillVerified"
                          : verificationUi.pillTone === "rejected"
                            ? "myAccountVerificationPillRejected"
                            : "myAccountVerificationPillPending"
                      }`}
                    >
                      {verificationUi.pillTone === "verified" ? (
                        <CheckCircleFilled />
                      ) : verificationUi.pillTone === "rejected" ? (
                        <ClockCircleOutlined />
                      ) : (
                        <ClockCircleOutlined />
                      )}
                      {verificationUi.pillLabel}
                    </span>
                  </div>
                  <p>{userData.email || "No email on file"}</p>
                </div>
              </div>

              <div className="myAccountOverviewGrid">
                <div className="myAccountOverviewRow">
                  <span>Account Type</span>
                  <strong className="myAccountValueWithIcon">
                    {String(userData.accountType || "").toLowerCase() ===
                    "business" ? (
                      <BankFilled />
                    ) : (
                      <UserOutlined />
                    )}
                    {userData.accountType || "N/A"}
                  </strong>
                </div>
                {userData.businessName ? (
                  <div className="myAccountOverviewRow">
                    <span>Business Name</span>
                    <strong>{userData.businessName}</strong>
                  </div>
                ) : null}
                <div className="myAccountOverviewRow">
                  <span>Name</span>
                  <strong>{userData.name || "N/A"}</strong>
                </div>
                <div className="myAccountOverviewRow">
                  <span>Email</span>
                  <strong>{userData.email || "N/A"}</strong>
                </div>
                <div className="myAccountOverviewRow">
                  <span>Phone Number</span>
                  <strong>{formatPhoneNumber(userData.phoneNumber)}</strong>
                </div>
                <div className="myAccountOverviewRow">
                  <span>Country</span>
                  <strong className="myAccountValueWithIcon">
                    {getCountryFlag(userData.country) ? (
                      <img
                        src={getCountryFlag(userData.country)}
                        alt={userData.country}
                        className="myAccountCountryFlag"
                      />
                    ) : null}
                    {userData.country || "N/A"}
                  </strong>
                </div>
              </div>

              <div className="myAccountActions">
                {!verificationUi.isVerified && verificationUi.showAction ? (
                  <Button
                    icon={<SafetyCertificateOutlined />}
                    className="appSecondaryBtn myAccountSecondaryBtn myAccountVerificationBtn"
                    onClick={handleStartVerification}
                  >
                    {verificationUi.actionLabel}
                  </Button>
                ) : null}
                <Button
                  icon={<LockOutlined />}
                  className="appSecondaryBtn myAccountSecondaryBtn"
                  onClick={() => setPasswordModalOpen(true)}
                >
                  Change Password
                </Button>
              </div>
            </>
          )}
        </div>
      </div>

      <Modal
        title={null}
        open={passwordModalOpen}
        onCancel={() => {
          setPasswordModalOpen(false);
          passwordForm.resetFields();
        }}
        footer={null}
        className="myAccountPasswordModal"
      >
        <div className="myAccountPasswordIntro">
          <span className="myAccountPasswordIntroIcon">
            <LockOutlined />
          </span>
          <div className="myAccountPasswordIntroText">
            <h3>Change Password</h3>
            <p>Update your password to keep your account secure.</p>
          </div>
        </div>
        <Form
          form={passwordForm}
          layout="vertical"
          requiredMark={false}
          className="myAccountPasswordForm"
        >
          <Form.Item
            name="currentPassword"
            label="Current Password"
            rules={[
              {
                required: true,
                message: "Please enter your current password.",
              },
            ]}
          >
            <Input.Password size="large" placeholder="Enter current password" />
          </Form.Item>

          <Form.Item
            name="newPassword"
            label="New Password"
            rules={[
              { required: true, message: "Please enter a new password." },
              {
                min: 8,
                message: "Password must be at least 8 characters.",
              },
            ]}
          >
            <Input.Password size="large" placeholder="Enter new password" />
          </Form.Item>

          <Form.Item
            name="confirmPassword"
            label="Confirm New Password"
            dependencies={["newPassword"]}
            rules={[
              { required: true, message: "Please confirm your new password." },
              ({ getFieldValue }) => ({
                validator(_, value) {
                  if (!value || getFieldValue("newPassword") === value) {
                    return Promise.resolve();
                  }
                  return Promise.reject(new Error("Passwords do not match."));
                },
              }),
            ]}
          >
            <Input.Password size="large" placeholder="Confirm new password" />
          </Form.Item>
        </Form>

        <div className="appModalActions myAccountPasswordActions">
          <Button
            className="appSecondaryBtn"
            onClick={() => {
              setPasswordModalOpen(false);
              passwordForm.resetFields();
            }}
          >
            Cancel
          </Button>
          <Button
            type="primary"
            className="appPrimaryBtn"
            loading={passwordSubmitting}
            onClick={handleChangePassword}
          >
            Update Password
          </Button>
        </div>
      </Modal>
    </Content>
  );
};
export default MyAccount;
